import { CloseOutlined, PlusOutlined, SaveFilled } from "@ant-design/icons"
import { Button, Flex, Form, Modal, Space } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import type { BaseOptionType } from "antd/lib/select"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { FormChangedWatcher } from "../../../../components/Forms/FormChangedWatcher/FormChangedWatcher.tsx"
import { TabbedEditForm } from "../../../../components/Forms/TabbedEditForm/TabbedEditForm.tsx"
import { type TabbedEditFormHandle } from "../../../../components/Forms/TabbedEditForm/tab-types.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { useNotifications } from "../../../../hooks/use-notifications.ts"
import {
  canViewFinanceSnapshot,
  canViewFinanceTab
} from "../../helpers/project-finance-visibility.ts"
import { type Project } from "../../types/project.types.ts"
import { ProjectSectionCard } from "./ProjectSectionCard.tsx"
import { type CustomerPick, type ProjectFormData } from "./project-form-data.ts"
import { buildProjectTabs } from "./project-form-tabs.tsx"
import { SummarySection } from "./sections/SummarySection.tsx"

interface ProjectFormProps {
  formInstance?: FormInstance<ProjectFormData>
  initialValues?: Partial<ProjectFormData>
  onSubmit: (data: ProjectFormData) => void
  isEdit?: boolean
  saving?: boolean
  options: Record<string, BaseOptionType[]>
  projectFull?: Project
}

export function ProjectForm({
  formInstance,
  onSubmit,
  initialValues,
  saving,
  isEdit,
  projectFull,
  options
}: ProjectFormProps) {
  const permissions = useAppSelector(
    (state) => state.auth?.userInfo?.permissions?.codes
  )
  // state.auth.permissions is the canonical copy; userInfo.permissions can go
  // stale after a token refresh whose payload omits them.
  const projectPermissions = useAppSelector(
    (state) => state.auth.permissions?.codes?.projects
  )
  const { normalizeDate } = useDateFormat()

  const notification = useNotifications()
  const [form] = Form.useForm<ProjectFormData>(formInstance)
  const [isDirty, setIsDirty] = useState(false)
  const [customerPick, setCustomerPick] = useState<CustomerPick | null>(null)
  const normalizedInitialValues = useMemo(
    () => ({ ...(initialValues ?? {}) }),
    [initialValues]
  )

  const assignedSalesAllowed =
    permissions?.leads?.features?.lead_assign_sales_manager
  const assignedPMAllowed = permissions?.projects?.features?.project_assign_pm
  const financeAllowed = canViewFinanceTab(projectPermissions)
  const snapshotAllowed = canViewFinanceSnapshot(projectPermissions)

  const { t } = useTranslation()
  const tabbedRef = useRef<TabbedEditFormHandle>(null)
  const tabs = buildProjectTabs({
    t,
    form,
    options,
    isEdit,
    saving,
    initialValues,
    projectFull,
    assignedSalesAllowed,
    assignedPMAllowed,
    financeAllowed,
    customerPick,
    onCustomerPicked: (zip: string | null) => setCustomerPick({ zip })
  })

  const onSubmitHandler = ({
    file_list,
    customer,
    start_date,
    end_date,
    project_phases,
    ...data
  }: ProjectFormData) => {
    const { status, ...safeCustomer } = customer ?? {}

    onSubmit({
      ...data,
      project_phases: project_phases?.map((p) => ({
        ...p,
        start_date: normalizeDate(p?.start_date),
        end_date: normalizeDate(p?.end_date)
      })),
      start_date: normalizeDate(start_date),
      end_date: normalizeDate(end_date),
      customer: safeCustomer,
      file_list: file_list?.map((file) => ({
        file_type: 5,
        file: "file_type" in file ? file?.file : file
      }))
    })
  }

  const onResetForm = () => {
    if (!initialValues) return reset()

    function reset() {
      form.resetFields()
      notification.success({
        message: "Form reset successfully",
        description: "All unsaved changes have been reset."
      })
      setTimeout(() => {
        void form.validateFields()
      }, 0)
    }

    Modal.confirm({
      title: "Confirm Reset",
      content:
        "Are you sure you want to reset the form? Unsaved changes will be lost.",
      onOk: reset,
      onCancel: () => {},
      okText: "Yes",
      cancelText: "No"
    })
  }

  useEffect(() => {
    void form.validateFields()
  }, [])

  return (
    <>
      <Form<ProjectFormData>
        form={form}
        clearOnDestroy
        onFinish={onSubmitHandler}
        onFinishFailed={(e) => tabbedRef.current?.goToFirstError(e.errorFields)}
        name="dynamic_form_item"
        layout="vertical"
        scrollToFirstError={{
          behavior: "smooth",
          block: "center",
          scrollMode: "always"
        }}
        className={
          "flex-1 flex flex-col w-full overflow-auto with-default-error"
        }
        validateTrigger={["onSubmit", "onChange"]}
        disabled={saving}
        initialValues={normalizedInitialValues}
        onValuesChange={(changed) => {
          if (changed.project_phases) {
            const hasError = form.getFieldError(["project_phases"])?.length > 0
            if (hasError) form.validateFields(["project_phases"])
          }
        }}
      >
        <FormChangedWatcher<ProjectFormData>
          form={form}
          saving={saving}
          onDirtyChange={setIsDirty}
        />

        <Flex gap={12} flex={1} className={"overflow-auto tailwind-scroll"}>
          <Flex
            vertical
            gap={"middle"}
            className={snapshotAllowed ? "xl:w-4/5 w-full" : "w-full"}
          >
            <TabbedEditForm ref={tabbedRef} tabs={tabs} />
            <Form.Item<ProjectFormData> name="activeProjectTypeId" hidden>
              <input />
            </Form.Item>
            <Form.Item<ProjectFormData> name="deleted_attachment_ids" hidden>
              <input />
            </Form.Item>
          </Flex>
          {snapshotAllowed && (
            <Flex className={"sticky top-0 w-1/5 max-xl:hidden"}>
              <ProjectSectionCard divider title={"Project Snapshot"}>
                <SummarySection form={form} />
              </ProjectSectionCard>
            </Flex>
          )}
        </Flex>

        <Flex
          justify={"end"}
          className={`sticky bottom-0 bg-background pr-2 xl:pr-4 w-full z-10 py-2 ${
            snapshotAllowed ? "xl:w-4/5" : ""
          }`}
        >
          <Space>
            {isEdit && (
              <Button
                disabled={saving || !isDirty}
                onClick={onResetForm}
                icon={<CloseOutlined />}
                variant={"solid"}
              >
                Cancel Changes
              </Button>
            )}
            <Button
              disabled={!isDirty}
              loading={saving}
              htmlType="submit"
              icon={isEdit ? <SaveFilled /> : <PlusOutlined />}
              variant={"solid"}
              color={"primary"}
            >
              {isEdit ? "Save Changes" : "Create Project"}
            </Button>
          </Space>
        </Flex>
      </Form>
    </>
  )
}
